import type { PipeStatEntry } from "./helper";

export type NormalizedStat = {
  key: string;
  title: string;
  homeValue: number;
  awayValue: number;
  displayType: "chart" | "line";
  valueFormatter?: (value: number) => string;
};

type ParsedStatValue = {
  value: number;
  rawDisplay: string;
  isPercentage: boolean;
};

const PERCENT_LABEL_REGEX =
  /%|PERCENT|CONVERSION|SUCCESS|RATE|ACCURACY|EFFICIENCY/i;

const FORCED_LINE_KEYS = new Set(["ACES", "DOUBLE FAULTS"]);
const FORCED_CHART_KEYS = new Set([
  "WIN % 1ST SERVE",
  "BREAK POINT CONVERSIONS",
]);

const parseStatValue = (
  rawValue: string,
  treatAsPercentage: boolean
): ParsedStatValue | null => {
  if (typeof rawValue !== "string") return null;
  const trimmed = rawValue.trim();
  if (!trimmed) return null;

  const percentMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*%$/);
  if (percentMatch) {
    const percent = Number(percentMatch[1]);
    if (Number.isFinite(percent)) {
      return {
        value: percent,
        rawDisplay: trimmed,
        isPercentage: true,
      };
    }
  }

  const direct = Number(trimmed);
  if (Number.isFinite(direct)) {
    return {
      value: direct,
      rawDisplay: trimmed,
      isPercentage: treatAsPercentage,
    };
  }

  if (trimmed.includes("/")) {
    const [madeRaw, totalRaw] = trimmed
      .split("/")
      .map((part) => part.trim())
      .slice(0, 2);
    const made = Number(madeRaw);
    const total = Number(totalRaw);
    if (Number.isFinite(made) && Number.isFinite(total) && total > 0) {
      return {
        value: (made / total) * 100,
        rawDisplay: `${made}/${total}`,
        isPercentage: true,
      };
    }
  }

  return null;
};

export const normalizeStats = (entries: PipeStatEntry[]): NormalizedStat[] => {
  if (!entries || entries.length === 0) return [];

  const normalized: NormalizedStat[] = [];

  entries.forEach(({ label, home, away }) => {
    const trimmedLabel = label.trim();
    const key = trimmedLabel.toUpperCase();
    const labelSuggestsPercent = PERCENT_LABEL_REGEX.test(key);

    const homeParsed = parseStatValue(home, labelSuggestsPercent);
    const awayParsed = parseStatValue(away, labelSuggestsPercent);

    if (!homeParsed || !awayParsed) return;

    const isPercentage =
      labelSuggestsPercent ||
      homeParsed.isPercentage ||
      awayParsed.isPercentage;

    const isRatio =
      homeParsed.rawDisplay.includes("/") ||
      awayParsed.rawDisplay.includes("/");

    const detailSuffix =
      isRatio && homeParsed.rawDisplay && awayParsed.rawDisplay
        ? ` (H: ${homeParsed.rawDisplay} | A: ${awayParsed.rawDisplay})`
        : "";

    const total = homeParsed.value + awayParsed.value;

    let displayType: NormalizedStat["displayType"] =
      !isPercentage && total > 0 ? "chart" : "line";

    if (FORCED_LINE_KEYS.has(key)) {
      displayType = "line";
    } else if (FORCED_CHART_KEYS.has(key)) {
      displayType = "chart";
    }

    const normalizedStat: NormalizedStat = {
      key,
      title: `${key}${detailSuffix}`,
      homeValue: homeParsed.value,
      awayValue: awayParsed.value,
      displayType,
    };

    if (isPercentage) {
      normalizedStat.valueFormatter = (value: number) =>
        Number.isFinite(value) ? `%${Math.round(value)}` : "-";
    }

    normalized.push(normalizedStat);
  });

  return normalized;
};
